import { Link } from "react-router-dom";
import { Linkedin, Instagram, Youtube, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "./Header";
import { useLead } from "./LeadDialog";
import { useSiteConfig, DEFAULT_FOOTER_COLUMNS, DEFAULT_FOOTER_CONTACTS } from "@/lib/siteConfig";
import SeoLinkHub from "./SeoLinkHub";

const LOUNGE_IMG = "https://images.unsplash.com/photo-1505409859467-3a796fd5798e?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

function WhatsAppIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2Zm5.8 14.13c-.24.68-1.42 1.32-1.95 1.36-.5.05-.99.24-3.35-.7-2.82-1.11-4.6-3.99-4.74-4.18-.14-.19-1.14-1.51-1.14-2.88 0-1.37.72-2.04.97-2.32.25-.28.55-.35.73-.35l.53.01c.17.01.4-.06.62.48.24.57.81 1.96.88 2.1.07.14.12.31.02.5-.09.19-.14.31-.28.48-.14.16-.29.37-.42.49-.14.14-.29.28-.12.56.17.28.75 1.24 1.62 2.01 1.11.99 2.05 1.3 2.34 1.44.28.14.45.12.62-.07.17-.19.71-.83.9-1.11.19-.28.38-.23.63-.14.25.09 1.62.76 1.9.9.28.14.46.21.53.33.07.12.07.68-.17 1.36Z" />
    </svg>
  );
}

function ContactRow({ item }) {
  const Ic = item.icon === "phone" ? <Phone className="w-4 h-4" /> : <WhatsAppIcon className="w-4 h-4" />;
  const content = (
    <>
      <span className="w-9 h-9 rounded-full border border-slate-200 text-slate-500 flex items-center justify-center shrink-0 group-hover:border-primary group-hover:text-primary transition-colors">{Ic}</span>
      <span className="text-sm text-slate-600 group-hover:text-primary transition-colors">{item.label}</span>
    </>
  );
  return item.href
    ? <a href={item.href} target={item.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="group flex items-center gap-3">{content}</a>
    : <div className="group flex items-center gap-3">{content}</div>;
}

export default function Footer({ settings = {} }) {
  const { openLead } = useLead();
  const cfg = useSiteConfig();
  const social = { ...(settings.social || {}), ...(cfg.social || {}) };
  const columns = cfg.footer_columns?.length ? cfg.footer_columns : DEFAULT_FOOTER_COLUMNS;
  const contacts = cfg.footer_contacts?.length ? cfg.footer_contacts : DEFAULT_FOOTER_CONTACTS;

  const title = (settings.final_cta_title || "Let's Build a Productive Workplace Together").trim();
  const words = title.split(" ");
  const lastWord = words.pop();
  const head = <>{words.join(" ")} <span className="text-primary">{lastWord}</span></>;

  return (
    <footer className="bg-white text-slate-600">
      {/* CTA band */}
      <div className="relative overflow-hidden bg-gradient-to-b from-[#eef3ff] via-[#f4f7ff] to-white">
        {/* decorative corner image + arc */}
        <div className="hidden xl:block absolute top-0 right-0 h-full w-[320px] pointer-events-none" aria-hidden="true">
          <div className="absolute -top-28 -right-24 w-[440px] h-[440px] rounded-full border-[3px] border-primary/25" />
          <div className="absolute top-0 right-0 w-[300px] h-[230px] rounded-bl-[130px] overflow-hidden shadow-sm">
            <img src={LOUNGE_IMG} alt="CoWorkZen lounge" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="hidden lg:block absolute bottom-10 right-[340px] w-24 h-14 opacity-70 pointer-events-none" aria-hidden="true"
          style={{ backgroundImage: "radial-gradient(#c7d2fe 1.6px, transparent 1.6px)", backgroundSize: "12px 12px" }} />

        <div className="container-cz relative py-14 sm:py-16 flex flex-col lg:flex-row lg:items-center justify-between gap-8 xl:pr-[300px]">
          <div className="max-w-xl">
            <p className="text-sm font-bold tracking-[0.16em] text-primary mb-3" data-testid="footer-eyebrow">WORK • CONNECT • GROW</p>
            <h3 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold text-slate-900 leading-[1.1]">{head}</h3>
            <p className="text-slate-500 mt-4 text-base sm:text-lg">{settings.final_cta_subtitle || "Discover flexible, future-ready workspaces designed for your team."}</p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Button data-testid="footer-find-workspace" onClick={() => (window.location.href = "/locations")}
              className="rounded-full h-12 px-7 bg-primary text-white hover:bg-primary/90 gap-2 font-semibold text-base shadow-sm">
              Find a Workspace <ArrowRight className="w-4 h-4" />
            </Button>
            <Button data-testid="footer-talk-team" onClick={() => openLead("enterprise")} variant="outline"
              className="rounded-full h-12 px-7 bg-white border-slate-300 text-slate-900 hover:bg-slate-50 font-semibold text-base">
              Talk to Our Team
            </Button>
          </div>
        </div>
      </div>

      {/* Footer main */}
      <div className="border-t border-slate-200">
        <div className="container-cz py-14 flex flex-col lg:flex-row gap-10">
          <div className="lg:w-[34%]">
            <Logo />
            <p className="text-sm text-slate-500 mt-4 max-w-sm leading-relaxed">CoWorkZen enables people to experience the benefits of a shared office environment when working on their individual projects.</p>
            <div className="mt-5 space-y-3">
              {contacts.map((item) => <ContactRow key={item.label} item={item} />)}
            </div>
            <div className="flex gap-3 mt-6">
              {[[Linkedin, social.linkedin, "linkedin"], [Instagram, social.instagram, "instagram"], [Youtube, social.youtube, "youtube"]]
                .filter(([, href]) => (href || "").trim())
                .map(([Icon, href, key]) => (
                <a key={key} href={href} target="_blank" rel="noreferrer" data-testid={`footer-social-${key}`}
                  className="w-10 h-10 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center hover:bg-primary hover:text-white transition-colors">
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-8 lg:border-l lg:border-slate-200 lg:pl-10">
            {columns.map((c) => (
              <div key={c.title}>
                <h4 className="text-[11px] font-bold tracking-[0.14em] uppercase text-slate-400 mb-4">{c.title}</h4>
                <ul className="space-y-3">
                  {(c.links || []).map((l) => {
                    const label = Array.isArray(l) ? l[0] : l.label;
                    const to = Array.isArray(l) ? l[1] : l.to;
                    const external = /^https?:\/\//.test(to || "") || (to || "").startsWith("/api");
                    return (
                      <li key={label}>
                        {external ? (
                          <a href={to} className="text-[15px] text-slate-600 hover:text-primary transition-colors whitespace-nowrap">{label}</a>
                        ) : (
                          <Link to={to} className="text-[15px] text-slate-600 hover:text-primary transition-colors whitespace-nowrap">{label}</Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* SEO internal-link hub (grouped by landing-page category) */}
      <SeoLinkHub />

      {/* Bottom bar */}
      <div className="border-t border-slate-200">
        <div className="container-cz py-5 flex flex-col sm:flex-row justify-between items-center gap-3 text-sm text-slate-400">
          <span>© {new Date().getFullYear()} {settings.brand_name || "CoworkZen"}. All rights reserved.</span>
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" data-testid="footer-cookie-prefs" className="hover:text-primary transition-colors">Cookie Preferences</Link>
            <a href="/api/seo/sitemap.xml" data-testid="footer-sitemap" className="hover:text-primary transition-colors">Sitemap</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
